<template>
  <div style="height: 100%;">
    <v-row class="mb-1">
      <v-col
        cols="12"
        sm="6"
        class="d-flex align-center"
      >
        <p class="text-h6 pl-1 pt-2">
          {{ $t('Preview') }}
        </p>
      </v-col>

      <v-col
        cols="12"
        sm="6"
      >
        <v-select
          v-show="languages.length > 1 && $root.preview.faq.component !== 'portal'"
          :value="$root.preview.faq.language"
          :items="languages"
          dense
          item-text="name"
          item-value="code"
          outlined
          hide-details
          @change="handleLanguageChange"
        />

        <div
          v-if="$root.preview.faq.component === 'portal' && portalEnabled"
          class="d-flex align-center justify-end"
          style="height: 100%"
        >
          <v-btn
            small
            text
            color="primary"
            :href="portalUrl"
            target="_blank"
          >
            {{ $t('Open in new tab') }}
          </v-btn>
        </div>
      </v-col>
    </v-row>

    <v-btn-toggle
      v-if="! hideComponentSwitch"
      v-model="$root.preview.faq.component"
      tile
      class="mb-0 flex-wrap"
      color="primary"
      group
      mandatory
    >
      <v-btn
        small
        :disabled="portalPasswordsEnabled"
        value="embed"
      >
        {{ $t('Embed on page') }}
      </v-btn>

      <v-btn
        small
        :disabled="portalPasswordsEnabled"
        value="widget"
      >
        {{ $t('Use as widget') }}
      </v-btn>

      <v-btn
        small
        value="portal"
      >
        {{ $t('FAQ Portal') }}
      </v-btn>
    </v-btn-toggle>

    <v-divider class="mt-5 mb-8" />

    <v-fade-transition hide-on-leave>
      <p
        v-if="$root.preview.faq.component === 'widget' && ! portalPasswordsEnabled"
        class="subtitle-1 text-center"
      >
        {{ $t('See the bottom right corner') }}
      </p>

      <div v-if="$root.preview.faq.component === 'portal'">
        <div
          v-if="portalEnabled"
          class="elevation-4 rounded-lg rounded-t-0"
        >
          <div
            class="outer dark"
            style="width: 100%"
          >
            <div class="dot red" />
            <div class="dot yellow" />
            <div class="dot green" />
          </div>
          <iframe
            :key="`portal-${$root.preview.faq.key}`"
            :src="portalUrl"
            style="height: 500px; max-height: 500px; width: 100%; border: 0;"
          />
        </div>
        <div
          v-else
          class="subtitle-1 text-center"
        >
          {{ $t('FAQ Portal is not enabled, you can') }}
          <inertia-link :href="route('faq-settings.portal', [faqId])">
            {{ $t('enable it here') }}
          </inertia-link>
        </div>
      </div>
    </v-fade-transition>

    <webcomet-faq
      v-if="$root.preview.faq.component === 'embed' && ! portalPasswordsEnabled"
      :key="`embed-${$root.preview.faq.key}`"
      class="rounded-lg"
      :disable-statistics="true"
      :faq-id="faqId"
      :language="$root.preview.faq.language"
    />

    <webcomet-faq-widget
      v-if="$root.preview.faq.component === 'widget' && ! portalPasswordsEnabled"
      :key="`widget-${$root.preview.faq.key}`"
      :disable-statistics="true"
      :faq-id="faqId"
      :language="$root.preview.faq.language"
    />
  </div>
</template>

<script>
export default {
  props: {
    faqId: {
      type: String,
      required: true
    },
    defaultLanguage: {
      type: String,
      required: true
    },
    languages: {
      type: Array,
      required: true
    },
    hideComponentSwitch: {
      type: Boolean,
    },
    portalUrl: {
      type: String,
      required: false,
      default: null
    },
    portalEnabled: {
      type: Boolean
    },
    portalPasswordsEnabled: {
      type: Boolean
    }
  },

  created() {
    if (this.$root.preview.faq.language === null) {
      this.$root.preview.faq.language = this.defaultLanguage;
    }

    if (this.portalPasswordsEnabled) {
      this.$root.preview.faq.component = 'portal';
    }
  },

  methods: {
    handleLanguageChange(language) {
      this.$root.preview.faq.language = language;
      this.$forceUpdate();
    }
  }
}
</script>

<style lang="scss">
  .outer {
    background: #b4b4b4;
    opacity: 0.9;
    width: 960px;
    margin: 10px auto 0 auto;
    padding: 10px;
    border-radius: 8px 8px 0px 0px;

    &.dark {
      background: #202124;

      .dot {
        background: #202124;
      }
    }

    &.yosemite {
      background: #f6f6f6; /* Old browsers */
      background: -moz-linear-gradient(top,  #f6f6f6 0%, #dadada 100%); /* FF3.6+ */
      background: -webkit-gradient(linear, left top, left bottom, color-stop(0%,#f6f6f6), color-stop(100%,#dadada)); /* Chrome,Safari4+ */
      background: -webkit-linear-gradient(top,  #f6f6f6 0%,#dadada 100%); /* Chrome10+,Safari5.1+ */
      background: -o-linear-gradient(top,  #f6f6f6 0%,#dadada 100%); /* Opera 11.10+ */
      background: -ms-linear-gradient(top,  #f6f6f6 0%,#dadada 100%); /* IE10+ */
      background: linear-gradient(to bottom,  #f6f6f6 0%,#dadada 100%); /* W3C */
      filter: progid:DXImageTransform.Microsoft.gradient( startColorstr='#f6f6f6', endColorstr='#dadada',GradientType=0 ); /* IE6-9 */
      border: 1px solid #d8d8d8;
      border-bottom: 1px solid #b4b4b4;
    }
  }

.dot {
  display: inline-block;
  width: 10px;
  height: 10px;
  background: #f9f9f9;
  border-radius: 50%;
  margin: 0 4px 0 0;

  &.red {
    background: #fe5e57;
    border: 1px solid #fe5e57;
  }

  &.amber {
    background: #febc2e;
    border: 1px solid #febc2e;
  }

  &.green {
    background: #2ac840;
    border: 1px solid #2ac840;
  }
}

webcomet-faq {
  background-image: url('data:image/png;base64,/9j/4AAQSkZJRgABAgAAZABkAAD/7AARRHVja3kAAQAEAAAAUAAA/+4AJkFkb2JlAGTAAAAAAQMAFQQDBgoNAAABpgAAAccAAAIDAAACMP/bAIQAAgICAgICAgICAgMCAgIDBAMCAgMEBQQEBAQEBQYFBQUFBQUGBgcHCAcHBgkJCgoJCQwMDAwMDAwMDAwMDAwMDAEDAwMFBAUJBgYJDQsJCw0PDg4ODg8PDAwMDAwPDwwMDAwMDA8MDAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwM/8IAEQgAFAAUAwERAAIRAQMRAf/EAH8AAAMBAQAAAAAAAAAAAAAAAAIDBAEIAQEAAAAAAAAAAAAAAAAAAAAAEAACAwEAAAAAAAAAAAAAAAAAATARMRARAQAAAAAAAAAAAAAAAAAAADASAQAAAAAAAAAAAAAAAAAAADATAAEEAwEAAAAAAAAAAAAAAAEAEDAx8BGxYf/aAAwDAQACEQMRAAAB7kHhgCzCgnBP/9oACAEBAAEFAhYUjC3x6f/aAAgBAgABBQKH/9oACAEDAAEFAof/2gAIAQICBj8CH//aAAgBAwIGPwIf/9oACAEBAQY/Ah//2gAIAQEDAT8hVTeSJJaFL2a5v//aAAgBAgMBPyGH/9oACAEDAwE/IYf/2gAMAwEAAhEDEQAAEBBJBB//2gAIAQEDAT8QXV1sRKOD6CgsQDcXG//aAAgBAgMBPxCH/9oACAEDAwE/EIf/2Q==');
  background-repeat: repeat;
  padding: 1rem;
}
</style>